import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    fetchReservableAvailability,
    fetchMicrosoftRoomMappings,
    fetchRoomsForOffice,
    fetchTagsForOffice,
    resolveRoomImageLinks,
    type RoomTag,
    type SessionClaims,
} from '../api'
import { checkRoomAvailabilities } from '../availability'
import { createDiagnosticID, diagnosticError, logDiagnostic } from '../diagnostics'
import { filterRoomGroups, mapSpacesToGroups, type Room, type RoomAvailability, type RoomGroup } from '../models/rooms'

const DEFAULT_LOADING_MESSAGE = 'Loading rooms...'
const ROOM_LOAD_FAILED_MESSAGE = 'Unable to load rooms'

function toggleGroupCollapsedState(groups: RoomGroup[], groupId: string) {
    return groups.map((group) => (group.id === groupId ? { ...group, collapsed: !group.collapsed } : group))
}

type UseRoomDataArgs = {
    enabled: boolean
    sessionToken: string
    sessionClaims: SessionClaims | null
    selectedOfficeId: string
    microsoftTenantId: string
    currentEventDate: string
    appointmentStart: Date | null
    appointmentEnd: Date | null
    searchTerm: string
    activeTagIds: Set<string>
    minCapacity: number | null
    onTagsLoaded: (tags: RoomTag[]) => void
}

function formatAvailabilityTime(date: Date) {
    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date)
}

function formatAvailabilityDayOfWeek(date: Date) {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
    }).format(date)
}

function createPendingAvailabilityMap(rooms: Room[]) {
    return Object.fromEntries(rooms.map((room) => [room.id, null])) as Record<string, RoomAvailability>
}

export function useRoomData({
    enabled,
    sessionToken,
    sessionClaims,
    selectedOfficeId,
    microsoftTenantId,
    currentEventDate,
    appointmentStart,
    appointmentEnd,
    searchTerm,
    activeTagIds,
    minCapacity,
    onTagsLoaded,
}: UseRoomDataArgs) {
    const requestIdRef = useRef(0)
    const availabilityRequestIdRef = useRef(0)

    const [availableTags, setAvailableTags] = useState<RoomTag[]>([])
    const [roomGroups, setRoomGroups] = useState<RoomGroup[]>([])
    const [allMappedRooms, setAllMappedRooms] = useState<Room[]>([])
    const [roomAvailabilityById, setRoomAvailabilityById] = useState<Record<string, RoomAvailability>>({})
    const [roomsLoadingMessage, setRoomsLoadingMessage] = useState(DEFAULT_LOADING_MESSAGE)
    const [roomsLoaded, setRoomsLoaded] = useState(false)

    const refreshOfficeData = useCallback(async () => {
        if (!enabled || !sessionToken || !sessionClaims || !selectedOfficeId || !microsoftTenantId) {
            logDiagnostic(
                'outlook_rooms_request_skipped',
                {
                    reason: !enabled
                        ? 'disabled'
                        : !sessionToken
                          ? 'missing_session'
                          : !sessionClaims
                            ? 'missing_claims'
                            : !selectedOfficeId
                              ? 'missing_office'
                              : 'missing_microsoft_tenant',
                },
                'WARN',
            )
            return
        }

        const requestId = ++requestIdRef.current
        const loadID = createDiagnosticID()
        const context = {
            loadID,
            companyID: sessionClaims.companyId,
            employeeID: sessionClaims.employeeId,
            officeID: selectedOfficeId,
            microsoftTenantID: microsoftTenantId,
        }
        let mappingsFailed = false
        logDiagnostic('outlook_rooms_request_started', context)
        setRoomsLoadingMessage(DEFAULT_LOADING_MESSAGE)
        setRoomsLoaded(false)

        try {
            const [roomsResponse, tagsResponse, roomMappingsResponse] = await Promise.all([
                fetchRoomsForOffice(sessionToken, sessionClaims.companyId, selectedOfficeId),
                fetchTagsForOffice(sessionToken, sessionClaims.companyId, selectedOfficeId),
                fetchMicrosoftRoomMappings(sessionToken, microsoftTenantId).catch((error) => {
                    mappingsFailed = true
                    logDiagnostic(
                        'outlook_room_mappings_request_failed',
                        { ...context, ...diagnosticError(error), reason: 'mapping_error_replaced_with_empty_list' },
                        'ERROR',
                    )
                    console.error('Failed to load Microsoft room mappings.', error)
                    return []
                }),
            ])

            logDiagnostic('outlook_rooms_dependencies_resolved', {
                ...context,
                spaceCount: roomsResponse.spaces.length,
                roomCount: roomsResponse.spaces.reduce(
                    (count, space) => count + (space.Reservables?.items || []).filter(Boolean).length,
                    0,
                ),
                tagCount: tagsResponse.length,
                mappingCount: roomMappingsResponse.length,
                mappingsFailed,
            })
            const roomsWithSignedImages = await resolveRoomImageLinks(sessionToken, roomsResponse)

            if (requestIdRef.current !== requestId) {
                logDiagnostic('outlook_rooms_request_skipped', { ...context, reason: 'superseded_load' })
                return
            }

            setAvailableTags(tagsResponse)
            onTagsLoaded(tagsResponse)

            const roomMappings = new Map<string, (typeof roomMappingsResponse)[number]>(
                roomMappingsResponse.map((mapping) => [mapping.reservableRoomId, mapping] as const),
            )
            const mappedGroups = mapSpacesToGroups(roomsWithSignedImages.spaces, roomMappings)

            const fetchedRooms = roomsResponse.spaces.flatMap((space) => space.Reservables?.items || []).filter(Boolean)
            const matchedCount = mappedGroups.reduce((count, group) => count + group.rooms.length, 0)
            logDiagnostic('outlook_rooms_decision_made', {
                ...context,
                matchedRoomCount: matchedCount,
                missingMappingCount: fetchedRooms.filter((room) => !roomMappings.has(room.id)).length,
                missingRoomEmailCount: fetchedRooms.filter(
                    (room) => roomMappings.has(room.id) && !roomMappings.get(room.id)?.roomEmail,
                ).length,
                reason: mappingsFailed
                    ? 'mapping_request_failed'
                    : !fetchedRooms.length
                      ? 'no_office_rooms'
                      : !matchedCount
                        ? 'no_usable_mapping_matches'
                        : 'rooms_matched',
            })
            logDiagnostic('outlook_rooms_request_succeeded', { ...context, matchedRoomCount: matchedCount })
            setRoomGroups(mappedGroups)
            setAllMappedRooms(mappedGroups.flatMap((group) => group.rooms))
            setRoomsLoaded(true)
        } catch (error) {
            logDiagnostic('outlook_rooms_request_failed', { ...context, ...diagnosticError(error) }, 'ERROR')
            console.error(error)
            if (requestIdRef.current === requestId) {
                setRoomGroups([])
                setAllMappedRooms([])
                setRoomAvailabilityById({})
                setRoomsLoadingMessage(ROOM_LOAD_FAILED_MESSAGE)
                setRoomsLoaded(true)
            }
        }
    }, [enabled, microsoftTenantId, onTagsLoaded, selectedOfficeId, sessionClaims, sessionToken])

    useEffect(() => {
        void refreshOfficeData()
    }, [refreshOfficeData])

    useEffect(() => {
        if (!enabled || !sessionToken || !sessionClaims?.employeeId || !allMappedRooms.length) {
            logDiagnostic('outlook_availability_request_skipped', {
                reason: 'missing_session_or_mapped_rooms',
                officeID: selectedOfficeId,
            })
            setRoomAvailabilityById({})
            return
        }

        if (!currentEventDate || !appointmentStart || !appointmentEnd) {
            logDiagnostic('outlook_availability_request_skipped', {
                reason: 'missing_appointment_time',
                officeID: selectedOfficeId,
            })
            setRoomAvailabilityById(createPendingAvailabilityMap(allMappedRooms))
            return
        }

        const requestId = ++availabilityRequestIdRef.current
        const nextPendingMap = createPendingAvailabilityMap(allMappedRooms)
        setRoomAvailabilityById(nextPendingMap)

        const availabilityContext = {
            officeID: selectedOfficeId,
            roomCount: allMappedRooms.length,
            availabilityID: createDiagnosticID(),
        }
        const loadAvailability = async () => {
            logDiagnostic('outlook_availability_request_started', availabilityContext)
            try {
                const dayOfWeek = formatAvailabilityDayOfWeek(appointmentStart)
                const startTime = formatAvailabilityTime(appointmentStart)
                const endTime = formatAvailabilityTime(appointmentEnd)

                const results = await checkRoomAvailabilities(
                    allMappedRooms.map((room) => room.id),
                    (roomId) =>
                        fetchReservableAvailability(sessionToken, {
                            reservableId: roomId,
                            employeeId: sessionClaims.employeeId,
                            date: currentEventDate,
                            dayOfWeek,
                            startTime,
                            endTime,
                            intervalStart: appointmentStart.toISOString(),
                            intervalEnd: appointmentEnd.toISOString(),
                        }),
                    (roomId, error) => {
                        logDiagnostic(
                            'outlook_availability_room_request_failed',
                            { ...availabilityContext, reservableID: roomId, ...diagnosticError(error) },
                            'ERROR',
                        )
                    },
                )

                if (availabilityRequestIdRef.current !== requestId) {
                    logDiagnostic('outlook_availability_request_skipped', {
                        ...availabilityContext,
                        reason: 'superseded_load',
                    })
                    return
                }

                const statuses = Object.values(results)
                const failedCount = statuses.filter((status) => status === 'error').length
                const availabilityEvent =
                    failedCount === statuses.length
                        ? 'outlook_availability_request_failed'
                        : failedCount
                          ? 'outlook_availability_request_partial_failed'
                          : 'outlook_availability_request_succeeded'
                logDiagnostic(
                    availabilityEvent,
                    {
                        ...availabilityContext,
                        availableCount: statuses.filter((status) => status === true).length,
                        failedCount,
                    },
                    failedCount === statuses.length ? 'ERROR' : failedCount ? 'WARN' : 'INFO',
                )
                setRoomAvailabilityById(results)
            } catch (error) {
                logDiagnostic(
                    'outlook_availability_request_failed',
                    { ...availabilityContext, ...diagnosticError(error) },
                    'ERROR',
                )
                console.error('Failed to load room availability.', error)

                if (availabilityRequestIdRef.current === requestId) {
                    setRoomAvailabilityById(nextPendingMap)
                }
            }
        }

        void loadAvailability()
    }, [
        allMappedRooms,
        appointmentEnd,
        appointmentStart,
        currentEventDate,
        enabled,
        selectedOfficeId,
        sessionClaims,
        sessionToken,
    ])

    const filteredGroups = useMemo(
        () =>
            filterRoomGroups({
                roomGroups,
                activeTagIds,
                minCapacity,
                searchTerm,
            }),
        [activeTagIds, minCapacity, roomGroups, searchTerm],
    )

    useEffect(() => {
        if (!roomsLoaded) return
        const visibleRoomCount = filteredGroups.reduce((count, group) => count + group.rooms.length, 0)
        logDiagnostic('outlook_rooms_filter_decision_made', {
            officeID: selectedOfficeId,
            matchedRoomCount: allMappedRooms.length,
            visibleRoomCount,
            activeTagCount: activeTagIds.size,
            searchActive: Boolean(searchTerm.trim()),
            minCapacity,
            reason: visibleRoomCount
                ? 'rooms_visible'
                : allMappedRooms.length
                  ? 'all_rooms_filtered'
                  : 'no_mapped_rooms',
        })
    }, [
        roomsLoaded,
        filteredGroups,
        allMappedRooms.length,
        selectedOfficeId,
        activeTagIds.size,
        searchTerm,
        minCapacity,
    ])

    const toggleGroup = useCallback((groupId: string) => {
        setRoomGroups((previous) => toggleGroupCollapsedState(previous, groupId))
    }, [])

    return {
        availableTags,
        allMappedRooms,
        filteredGroups,
        roomAvailabilityById,
        roomsLoadingMessage,
        roomsLoaded,
        toggleGroup,
    }
}
